// Chart kit for the fitness pages: scales, ticks, hover, frames, tooltips,
// panels, and sport metadata. Every chart is plain SVG in a W×H viewBox.
import { useEffect, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import {
  MdBolt,
  MdDirectionsBike,
  MdDirectionsRun,
  MdDirectionsWalk,
  MdHiking,
  MdPool,
  MdSportsSoccer,
  MdSportsTennis,
} from "react-icons/md";

import {
  ACCENT,
  BLUE,
  DIM,
  FG,
  GOLD,
  GRID,
  HEAT,
  INSET,
  MINT,
  MONO,
  MUTED,
  PANEL,
  PANEL2,
  RED,
  VIOLET,
  alpha,
} from "../theme";

export * from "../theme";

export const SPORT_COLORS = {
  Tennis: ACCENT,
  Soccer: BLUE,
  Running: HEAT,
  Cycling: GOLD,
  Walking: VIOLET,
  Hiking: MINT,
  Swimming: "#4fd1e8",
  Other: "#8b8981",
};
// emoji glyphs — still used on the canvas share cards
export const GLYPH = {
  Tennis: "🎾",
  Soccer: "⚽",
  Running: "🏃",
  Cycling: "🚴",
  Walking: "🚶",
  Hiking: "🥾",
  Swimming: "🏊",
  Other: "▲",
};
const ICONS = {
  Tennis: MdSportsTennis,
  Soccer: MdSportsSoccer,
  Running: MdDirectionsRun,
  Cycling: MdDirectionsBike,
  Walking: MdDirectionsWalk,
  Hiking: MdHiking,
  Swimming: MdPool,
  Other: MdBolt,
};
export const sportColor = (g) => SPORT_COLORS[g] || SPORT_COLORS.Other;

export const SportIcon = ({ group, size = 16, color }) => {
  const Icon = ICONS[group] || ICONS.Other;
  return <Icon size={size} color={color || sportColor(group)} aria-hidden="true" />;
};

// round sport badge: icon on a tinted disc
export const SportBadge = ({ group, size = 32 }) => (
  <Flex
    w={`${size}px`}
    h={`${size}px`}
    borderRadius="full"
    align="center"
    justify="center"
    bg={alpha(sportColor(group), 0.14)}
    flexShrink={0}
  >
    <SportIcon group={group} size={size * 0.55} />
  </Flex>
);

// HR zone colors z1..z5
export const ZONE_COLORS = [BLUE, MINT, GOLD, "#ff9a3c", RED];
export const ZONE_NAMES = ["Easy", "Aerobic", "Tempo", "Threshold", "Max"];

// ---- chart geometry ----
export const W = 700;
export const H = 240;
export const ML = 44;
export const MR = 16;
export const MT = 16;
export const MB = 28;

export const fmtDate = (iso) =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

export const fmtLongDate = (iso) =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

// "synced 12 min ago", ticking; falls back to the date for old exports
export const useSynced = (data) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  if (!data || !data.generated) return null;
  if (!data.generatedAt) return fmtDate(data.generated);
  const mins = Math.round((Date.now() - new Date(data.generatedAt).getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h ago`;
  return fmtDate(data.generatedAt);
};

export const fmtDur = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(Math.round(secs % 60)).padStart(2, "0")}s`;
};

export const fmtPaceMin = (v) =>
  `${Math.floor(v)}:${String(Math.round((v % 1) * 60)).padStart(2, "0")}`;

export const linePath = (pts) =>
  pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join("");

// monotone cubic interpolation — smooth curves that never overshoot the data
export const smoothPath = (pts) => {
  const n = pts.length;
  if (n < 3) return linePath(pts);
  const dx = [];
  const m = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0];
    m[i] = (pts[i + 1][1] - pts[i][1]) / (dx[i] || 1);
  }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      t[i] = 0;
      t[i + 1] = 0;
      continue;
    }
    const a = t[i] / m[i];
    const b = t[i + 1] / m[i];
    const s = a * a + b * b;
    if (s > 9) {
      const k = 3 / Math.sqrt(s);
      t[i] = k * a * m[i];
      t[i + 1] = k * b * m[i];
    }
  }
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += `C${(pts[i][0] + h).toFixed(1)},${(pts[i][1] + t[i] * h).toFixed(1)} ${(pts[i + 1][0] - h).toFixed(1)},${(
      pts[i + 1][1] - t[i + 1] * h
    ).toFixed(1)} ${pts[i + 1][0].toFixed(1)},${pts[i + 1][1].toFixed(1)}`;
  }
  return d;
};

export const makeScale = (lo, hi, outLo, outHi) => {
  const d = hi - lo || 1;
  return (v) => outLo + ((v - lo) / d) * (outHi - outLo);
};

export const niceTicks = (lo, hi, n = 4) => {
  const raw = (hi - lo || 1) / n;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const out = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(+v.toFixed(6));
  return out;
};

export const pickIdx = (n, want = 5) => {
  if (n <= want) return [...Array(n).keys()];
  const out = [];
  for (let k = 0; k < want; k++) out.push(Math.round((k * (n - 1)) / (want - 1)));
  return [...new Set(out)];
};

// pointer-driven hover (mouse + touch drag), snapped to the nearest index
export const useHover = (n, xForI, width = W) => {
  const [hi, setHi] = useState(null);
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const vx = ((e.clientX - rect.left) / rect.width) * width;
    let best = 0;
    let bd = Infinity;
    for (let i = 0; i < n; i++) {
      const d = Math.abs(xForI(i) - vx);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    setHi(best);
  };
  return [hi, onMove, () => setHi(null)];
};

// props for an <svg> that reports pointer hover
export const hoverProps = (onMove, onLeave) => ({
  onPointerMove: onMove,
  onPointerDown: onMove,
  onPointerLeave: onLeave,
  style: { touchAction: "pan-y", display: "block", overflow: "visible" },
});

const TICK = { className: "tick", fill: DIM, fontSize: 10, fontFamily: MONO, letterSpacing: "0.04em" };

export const Frame = ({ yTicks, sy, yFmt = (v) => v, xLabels, rightTicks, syR, rFmt, w = W, h = H }) => (
  <g>
    {yTicks.map((t) => (
      <g key={t}>
        <line x1={ML} x2={w - MR} y1={sy(t)} y2={sy(t)} stroke={GRID} strokeWidth={0.8} strokeDasharray="2 4" />
        <text x={ML - 8} y={sy(t) + 3.5} textAnchor="end" {...TICK}>
          {yFmt(t)}
        </text>
      </g>
    ))}
    {rightTicks &&
      rightTicks.map((t) => (
        <text key={`r${t}`} x={w - MR + 5} y={syR(t) + 3.5} textAnchor="start" {...TICK}>
          {rFmt(t)}
        </text>
      ))}
    {xLabels.map((l) => (
      <text key={l.x} x={l.x} y={h - 8} textAnchor="middle" {...TICK}>
        {l.text}
      </text>
    ))}
  </g>
);

export const Tooltip = ({ x, title, rows, w = W }) => (
  <Box
    position="absolute"
    left={`${(x / w) * 100}%`}
    top="0"
    transform={x > w * 0.62 ? "translateX(calc(-100% - 12px))" : "translateX(12px)"}
    bg={alpha(PANEL2, 0.94)}
    backdropFilter="blur(8px)"
    border={`1px solid ${GRID}`}
    borderRadius="lg"
    px={3}
    py={2.5}
    pointerEvents="none"
    zIndex={5}
    whiteSpace="nowrap"
    boxShadow="0 12px 32px rgba(0,0,0,0.45)"
    minW="150px"
  >
    <Text color={FG} fontSize="xs" fontWeight={600} mb={1.5}>
      {title}
    </Text>
    {rows.map(([label, val, color]) => (
      <Flex key={label} align="center" gap={2} py="1px">
        <Box w="7px" h="7px" borderRadius="2px" bg={color} />
        <Text color={MUTED} fontSize="xs">
          {label}
        </Text>
        <Text color={FG} fontSize="xs" fontFamily={MONO} ml="auto" pl={4}>
          {val}
        </Text>
      </Flex>
    ))}
  </Box>
);

export const Crosshair = ({ x, top = MT, bottom = H - MB }) => (
  <line x1={x} x2={x} y1={top} y2={bottom} stroke={alpha(FG, 0.35)} strokeWidth={1} strokeDasharray="3 3" />
);

// the standard chart card
export const Panel = ({ title, kicker, caption, children, right, accent, ...rest }) => (
  <Box
    bg={PANEL}
    border={`1px solid ${GRID}`}
    borderRadius="2xl"
    p={{ base: 4, md: 6 }}
    mb={5}
    position="relative"
    overflow="hidden"
    {...rest}
  >
    {accent && <Box position="absolute" top={0} left={0} right={0} h="2px" bg={accent} />}
    {(title || right || kicker) && (
      <Flex justify="space-between" align="flex-start" mb={4} gap={4}>
        <Box>
          {kicker && (
            <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.14em" textTransform="uppercase" color={MUTED} mb={1}>
              {kicker}
            </Text>
          )}
          {title && (
            <Text color={FG} fontSize={{ base: "md", md: "lg" }} fontWeight={600} letterSpacing="-0.02em">
              {title}
            </Text>
          )}
        </Box>
        {right}
      </Flex>
    )}
    {children}
    {caption && (
      <Text color={MUTED} fontSize="sm" mt={4} lineHeight="1.6" maxW="760px">
        {caption}
      </Text>
    )}
  </Box>
);

export const StatCard = ({ value, label, sub, color = FG, ...rest }) => (
  <Box bg={PANEL} border={`1px solid ${GRID}`} borderRadius="xl" p={4} {...rest}>
    <Text
      fontFamily={MONO}
      fontSize="10px"
      letterSpacing="0.12em"
      textTransform="uppercase"
      color={MUTED}
      mb={2}
      noOfLines={1}
    >
      {label}
    </Text>
    <Text color={color} fontSize={{ base: "xl", md: "2xl" }} fontWeight={600} letterSpacing="-0.03em" lineHeight="1.1">
      {value}
    </Text>
    {sub && (
      <Text color={MUTED} fontSize="xs" mt={1}>
        {sub}
      </Text>
    )}
  </Box>
);

export const Legend = ({ items }) => (
  <Flex gap={5} mt={3} wrap="wrap">
    {items.map(([color, label, dashed]) => (
      <Flex key={label} align="center" gap={2}>
        {dashed ? (
          <Box w="14px" borderTop={`2px dashed ${color}`} />
        ) : (
          <Box w="10px" h="10px" borderRadius="3px" bg={color} />
        )}
        <Text color={MUTED} fontSize="xs">
          {label}
        </Text>
      </Flex>
    ))}
  </Flex>
);

export const TrackSvg = ({ track, stroke, strokeWidth = 0.035, glow }) => (
  <svg viewBox="-1.15 -1.15 2.3 2.3" width="100%" height="100%">
    {glow && (
      <path
        d={track.map((p, i) => `${i ? "L" : "M"}${p[0]},${-p[1]}`).join("")}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth * 4}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.12}
      />
    )}
    <path
      d={track.map((p, i) => `${i ? "L" : "M"}${p[0]},${-p[1]}`).join("")}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx={track[0][0]} cy={-track[0][1]} r={strokeWidth * 1.4} fill={FG} />
  </svg>
);

export const HeatmapSvg = ({ tracks, color = ACCENT }) => (
  <svg viewBox="-1.1 -1.1 2.2 2.2" width="100%" height="100%" aria-label="Every GPS track, overlaid">
    {tracks.map((t, i) => (
      <path
        key={i}
        d={t.map((p, j) => `${j ? "L" : "M"}${p[0]},${-p[1]}`).join("")}
        fill="none"
        stroke={color}
        strokeWidth={0.0055}
        opacity={0.2}
        strokeLinejoin="round"
      />
    ))}
  </svg>
);

// normalize raw [lat, lon] geo points into a [-1, 1] track for TrackSvg
export const geoToTrack = (geo) => {
  if (!geo) return null;
  const pts = geo.filter(Boolean).map((p) => [Number(p[0]), Number(p[1])]);
  if (pts.length < 10) return null;
  const mlat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const mlon = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const cos = Math.cos((mlat * Math.PI) / 180);
  const xs = pts.map((p) => (p[1] - mlon) * cos);
  const ys = pts.map((p) => p[0] - mlat);
  const m = Math.max(...xs.map(Math.abs), ...ys.map(Math.abs)) || 1;
  return xs.map((x, i) => [x / m, ys[i] / m]);
};

// map an HR value to a zone color
export const hrColor = (hr, hrMax) => {
  if (hr == null) return MUTED;
  const r = hr / hrMax;
  if (r < 0.6) return ZONE_COLORS[0];
  if (r < 0.7) return ZONE_COLORS[1];
  if (r < 0.8) return ZONE_COLORS[2];
  if (r < 0.9) return ZONE_COLORS[3];
  return ZONE_COLORS[4];
};

export const InsetBox = (props) => <Box bg={INSET} borderRadius="xl" {...props} />;
